import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { openPanelChat } from "../infrastructure/panel-chat-connection";
import { mergeBoothContacts, openAdminBoothChat } from "../api/admin-booth-directory";
import { mergeHistoryPage } from "../lib/history-page";

/** A modal owns one connection, one booth scope, and a finite history/send lifecycle. */
export function useBoothConversation(booth, renderKey) {
  const { token, userId } = useSelector((state) => state.user);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: true, connected: false, selected: {}, messages: [], error: "" });
  const session = useRef(null);
  const pending = useRef(null);
  const sending = useRef(false);
  function settle(success) {
    if (!pending.current) return;
    clearTimeout(pending.current.timeout); pending.current.resolve(success); pending.current = null;
  }
  useEffect(() => {
    const current = { disposed: false, ready: false, selected: {}, controller: new AbortController() };
    session.current = current; sending.current = false;
    setState({ loading: true, connected: false, selected: {}, messages: [], error: "" });
    let timer, connection, requestedId, historyPage = 1;
    const finishLoading = () => { clearTimeout(timer); setState((value) => ({ ...value, loading: false })); };
    function fail(message) {
      if (current.disposed) return;
      clearTimeout(timer); current.ready = false; connection?.close(); settle(false);
      setState((value) => ({ ...value, loading: false, connected: false, error: message }));
    }
    function waitForHistory() {
      clearTimeout(timer); timer = setTimeout(() => fail("دریافت پیام‌ها انجام نشد؛ دوباره تلاش کنید."), 15000);
    }
    current.fail = fail;
    if (!token || !(Number(booth?.userId) > 0) || Number(booth.userId) === Number(userId)) {
      fail(!token ? "برای گفتگو وارد شوید." : Number(booth?.userId) === Number(userId)
        ? "ارسال پیام به غرفه خودتان امکان‌پذیر نیست." : "صاحب این غرفه مشخص نیست؛ گفتگو در دسترس نیست.");
      return () => { current.disposed = true; current.controller.abort(); };
    }
    waitForHistory();
    connection = openPanelChat(process.env.REACT_APP_API_CHAT_URL, token, {
      onReady: () => { if (!current.disposed) { current.ready = true; setState((value) => ({ ...value, connected: true })); } },
      onError: fail,
      onFrame: (frame) => {
        if (current.disposed) return;
        const payload = frame.arguments?.[1];
        if (Number(payload?.code) !== 200) { fail(payload?.message || "دریافت گفتگو انجام نشد."); return; }
        if (frame.target === "GetAll") {
          if (!Array.isArray(payload.data)) { fail("پاسخ گفتگو معتبر نیست."); return; }
          const contact = mergeBoothContacts([booth], payload.data, userId)[0];
          if (contact?.id) {
            current.selected = contact;
            setState((value) => ({ ...value, selected: contact }));
            if (requestedId !== contact.id) {
              requestedId = contact.id; waitForHistory();
              connection.send(JSON.stringify({ target: "GetMessage", arguments: [{ chatId: contact.id, page: 1, limit: 100 }] }));
            }
          } else if (!current.selected.id) {
            finishLoading(); setState((value) => ({ ...value, messages: [] }));
          }
        } else if (frame.target === "GetMessage") {
          if (Number(payload.chatId) !== Number(current.selected.id)) return;
          if (!Array.isArray(payload.data) || payload.data.some((row) => Number(row.chatId) !== Number(current.selected.id))) {
            fail("پاسخ پیام‌ها متعلق به این گفتگو نیست."); return;
          }
          let history;
          try { history = mergeHistoryPage(current.messages || [], payload.data, current.selected.id, payload.refresh ? Infinity : 100); }
          catch (reason) { fail(reason.message); return; }
          current.messages = history.data;
          setState((value) => ({ ...value, messages: history.data }));
          if (history.hasMore) {
            historyPage++; waitForHistory();
            connection.send(JSON.stringify({ target: "GetMessage", arguments: [{ chatId: current.selected.id, page: historyPage, limit: 100 }] }));
          } else finishLoading();
          const own = payload.data.find((row) => Number(row.userId) === Number(userId) && row.message === pending.current?.text && Number(row.id) > pending.current?.beforeId);
          if (own) settle(true);
        } else if (frame.target === "SendMessage") {
          const message = payload.data;
          if (Number(message?.chatId) !== Number(current.selected.id)) return;
          current.messages = [message, ...(current.messages || []).filter((row) => row.id !== message.id)];
          setState((value) => ({ ...value, messages: current.messages }));
          if (Number(message.userId) === Number(userId) && message.message === pending.current?.text && Number(message.id) > pending.current?.beforeId) settle(true);
        }
      },
    });
    current.connection = connection;
    return () => {
      current.disposed = true; current.ready = false; clearTimeout(timer);
      current.controller.abort(); connection.close(); settle(false);
    };
    // Each modal opening starts a new isolated session; frames retain that booth's scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId, booth?.id, booth?.userId, renderKey, attempt]);

  async function sendMessage(text) {
    const current = session.current;
    if (!current?.ready || current.disposed || sending.current || !text.trim()) return false;
    sending.current = true;
    let createTimeout;
    try {
      let chat = current.selected;
      if (!chat.id) {
        createTimeout = setTimeout(() => { current.controller.abort(); current.fail("ایجاد گفتگو انجام نشد؛ دوباره تلاش کنید."); }, 15000);
        chat = await openAdminBoothChat(token, { userId: booth.userId }, current.controller.signal);
        clearTimeout(createTimeout);
        if (current.disposed || !current.ready || current.controller.signal.aborted) return false;
        current.selected = chat; setState((value) => ({ ...value, selected: chat }));
        current.connection.send(JSON.stringify({ target: "NotifyChatCreated", arguments: [chat.id] }));
      }
      return await new Promise((resolve) => {
        pending.current = { resolve, text, beforeId: Math.max(0, ...(current.messages || []).map((row) => Number(row.id) || 0)),
          timeout: setTimeout(() => { if (!current.disposed) setState((value) => ({ ...value, error: "تأیید ارسال دریافت نشد؛ پیش از ارسال مجدد، گفتگو را به‌روز کنید." })); settle(false); }, 15000) };
        current.connection.send(JSON.stringify({ target: "SendMessage", arguments: [{ chatId: chat.id, message: text }] }));
      });
    } catch (reason) {
      if (!current.disposed) current.fail(reason.message || "ارسال پیام انجام نشد.");
      return false;
    } finally { clearTimeout(createTimeout); if (session.current === current) sending.current = false; }
  }
  return { ...state, currentUserId: userId, sendMessage, retry: () => setAttempt((value) => value + 1) };
}
